/* eslint-disable no-unused-vars */
import { useEffect, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  Paper,
  Divider,
  Card,
  Alert,
  AlertTitle,
  Stack,
} from '@mui/material';
import tenant from '@/services/TenantService';
import { toast } from 'react-toastify';
import ToastMessage from '@/components/toasts/ToastMessage';
import InfoIcon from '@mui/icons-material/Info';
import { useNavigate } from 'react-router-dom';
import { VoiceOverOff } from '@mui/icons-material';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY);

const PaymentForm = ({ onPaymentMethodSaved }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [elementsLoaded, setElementsLoaded] = useState(false);

  useEffect(() => {
    if (elements) {
      const checkElements = async () => {
        try {
          await elements.fetchUpdates();
          setElementsLoaded(true);
        } catch (error) {
          toast.error(
            <ToastMessage
              title="Fehler"
              subtitle="Fehler beim Laden der Zahlungsinformationen"
            />,
          );
        }
      };

      checkElements();
    }
  }, [elements]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setIsSubmitting(true);
    const { error, setupIntent } = await stripe.confirmSetup({
      elements,
      confirmParams: {
        return_url: window.location.href,
      },
      redirect: 'if_required',
    });

    if (error) {
      toast.error(<ToastMessage title="Fehler" subtitle={error.message} />);
      setIsSubmitting(false);
      return;
    }

    if (setupIntent?.status === 'succeeded') {
      toast.success(<ToastMessage title="Zahlungsmethode gespeichert" />);
      onPaymentMethodSaved(setupIntent.payment_method);
    } else {
      toast.error(
        <ToastMessage title="Fehler" subtitle="SetupIntent fehlgeschlagen" />,
      );
    }

    setIsSubmitting(false);
  };

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate autoComplete="off">
      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, mb: 2 }}>
        <Typography
          variant="subtitle1"
          fontWeight={500}
          mb={2}
          color="primary.main"
        >
          Zahlungsinformationen
        </Typography>

        <Typography variant="body2" color="text.secondary" mb={3}>
          Bitte geben Sie Ihre Kreditkarteninformationen ein, um Ihr Abonnement
          zu aktivieren. Die Karte wird erst belastet, wenn Sie kostenpflichtige
          Anwendungen hinzubuchen.
        </Typography>

        <Box sx={{ mb: 3 }}>
          <PaymentElement />
        </Box>

        <Box mt={3} display="flex" justifyContent="flex-end">
          {elementsLoaded ? (
            <Button
              type="submit"
              variant="contained"
              disabled={!stripe || isSubmitting}
              size="large"
            >
              {isSubmitting ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Zahlungspflichtig abonnieren'
              )}
            </Button>
          ) : (
            <CircularProgress size={24} />
          )}
        </Box>
      </Paper>

      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          Ihre Zahlung wird sicher über Stripe verarbeitet. Ihre Kartendaten
          werden nicht auf unseren Servern gespeichert.
        </Typography>
      </Box>
    </Box>
  );
};

const RegisterPaymentForm = ({ tenantId, userId, onSuccess }) => {
  const navigate = useNavigate();
  const [clientSecret, setClientSecret] = useState(null);
  const [priceBreakdown, setPriceBreakdown] = useState(null);
  const [totalAmount, setTotalAmount] = useState(null);
  const [uid, setUid] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [options, setOptions] = useState(null);
  const [invoice, setInvoice] = useState(false);
  const registrationToken = localStorage.getItem('registrationToken');

  useEffect(() => {
    const fetchSetupIntent = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const res = await tenant.post('/create-register-setup-intent', {
          tenantId,
          userId,
          app: import.meta.env.VITE_CLIENT_ID,
        }, {
          headers: {
            Authorization: `Bearer ${registrationToken}`,
          },
        });
        setClientSecret(res?.data?.clientSecret);
        setPriceBreakdown(res?.data?.priceBreakdown);
        setTotalAmount(res?.data?.totalAmount);
        setUid(res?.data?.uid);
        setOptions({
          clientSecret: res?.data?.clientSecret,
          appearance: {
            theme: 'stripe',
          },
        });
      } catch (err) {
        setError(
          err?.response?.data?.message ||
            'SetupIntent konnte nicht erstellt werden',
        );
        toast.error(
          <ToastMessage
            title="Fehler"
            subtitle="SetupIntent konnte nicht erstellt werden"
          />,
        );
      } finally {
        setIsLoading(false);
      }
    };
    fetchSetupIntent();
    const handleKeyDown = (e) => {
      if (e.key === ':' && e.ctrlKey && e.shiftKey) {
        setInvoice((prev) => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [tenantId, userId]);

  const handlePaymentMethodSaved = async (paymentMethodId) => {
    try {
      setIsLoading(true);
      const res = await tenant.post('/create-register-subscription', {
        tenantId,
        paymentMethodId,
        invoice,
        userId,
        appId: import.meta.env.VITE_CLIENT_ID,
        uid,
      }, {
        headers: {
          Authorization: `Bearer ${registrationToken}`,
        },
      });

      if (res.status === 204) {
        toast.success(<ToastMessage title="Abo erfolgreich erstellt" />);
        onSuccess();
      }
    } catch (err) {
      toast.error(
        <ToastMessage
          title="Fehler"
          subtitle={
            err?.response?.data?.message || 'Abo konnte nicht erstellt werden'
          }
        />,
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight={200}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Stack
        direction="column"
        justifyContent="center"
        alignItems="center"
        spacing={2}
        minHeight={200}
        textAlign="center"
        p={2}
      >
        <Typography color="error.main" gutterBottom>
          Es ist ein Fehler aufgetreten: {error}
        </Typography>
        <Button
          variant="outlined"
          color="primary"
          onClick={() => window.location.reload()}
        >
          Erneut versuchen
        </Button>
      </Stack>
    );
  }

  if (!options || !options.clientSecret) {
    return null;
  }

  return (
    <Elements stripe={stripePromise} options={options}>
      <Box
        sx={{
          p: { xs: 1, sm: 2 },
          width: '100%',
          maxWidth: 800,
          mx: 'auto',
          minWidth: 400,
        }}
      >
        <Card variant="outlined" sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom color="primary.main">
            Abo-Zusammenfassung
          </Typography>

          <Stack spacing={1}>
            {priceBreakdown.map((item, idx) => (
              <Box
                key={idx}
                display="flex"
                justifyContent="space-between"
                sx={{ py: 1 }}
              >
                <Typography>
                  {item.name} × {item.quantity}
                </Typography>
                <Typography>{(item.subtotal / 100).toFixed(2)} €</Typography>
              </Box>
            ))}
          </Stack>

          <Divider sx={{ my: 2 }} />

          <Box display="flex" justifyContent="space-between" mt={1}>
            <Typography fontWeight="bold">Gesamt:</Typography>
            <Typography fontWeight="bold" color="primary.main">
              {(totalAmount / 100).toFixed(2)} €
            </Typography>
          </Box>
        </Card>

        {!invoice && <PaymentForm onPaymentMethodSaved={handlePaymentMethodSaved} />}
        {invoice && (<>
          <Alert severity="info" icon={<InfoIcon />} variant="outlined">
            <AlertTitle>Rechnung angefordert</AlertTitle>
            <Typography variant="body2">
              Sie haben die Option zur Rechnungsstellung gewählt. Ein
              Mitarbeiter wird sich in Kürze mit Ihnen in Verbindung setzen, um
              die Details zu klären.
            </Typography>
          </Alert>
          <Button variant="contained" fullWidth sx={{ mt: 2 }} onClick={() => handlePaymentMethodSaved()}>
            Zahlungspflichtig registrieren
          </Button>
        </>
        )}
      </Box>
    </Elements>
  );
};

export default RegisterPaymentForm;
